import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessagesSquare, Undo2, Heart, BadgeDollarSign,
} from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: MessagesSquare, title: "Plain language",   body: "We explain everything clearly. No jargon, no buzzwords." },
  { icon: Undo2,          title: "No disruption",    body: "We improve what works. We don't replace what you've built." },
  { icon: Heart,          title: "Fully reversible", body: "Decide it's not for you? Switch back instantly. No penalties." },
  { icon: BadgeDollarSign,title: "One transparent price", body: "$29/month base. + $9 per additional seat. No upsells, no implementation fees, no surprises." },
];

const EASE = [0.22, 1, 0.36, 1];

export default function About() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAF8F5] text-slate-900 dark:bg-[#0b1220] dark:text-white">
      <div className="aurora opacity-40" />
      <div className="grain" />
      <PublicNav />

      <section className="relative">
        <div className="mx-auto max-w-3xl px-6 pt-20 pb-12 sm:px-8 sm:pt-28 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300" data-testid="about-eyebrow">About Symplee</div>
            <h1 className="font-display mt-3 text-5xl font-light leading-[1.1] tracking-tight sm:text-6xl" data-testid="about-headline">
              Why we built <span className="brand-text">Symplee</span>.
            </h1>
            <p
              className="font-display mt-8 text-2xl font-light leading-snug text-slate-900 dark:text-white sm:text-3xl"
              data-testid="about-lead-punch"
            >
              We&rsquo;re sick of everyone pretending they know what AI means.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-3xl px-6 pb-16 sm:px-8 lg:px-12">
          <article
            className="space-y-6 text-lg leading-[1.75] text-slate-800 dark:text-white/80"
            data-testid="about-manifesto"
          >
            <p>
              Over the past year, we&rsquo;ve spoken with hundreds of business owners and heard the same thing over and over:
            </p>

            <motion.blockquote
              initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE }}
              className="my-8 border-l-4 border-indigo-500 pl-6 italic text-xl leading-[1.5] text-slate-900 dark:text-white sm:text-2xl"
              data-testid="about-pull-quote"
            >
              &ldquo;I know I should probably be using AI, but I have no idea where to start.&rdquo;
            </motion.blockquote>

            <p>
              Every tool seems to require a sales call. Every website is filled with buzzwords nobody understands. Every demo
              promises to revolutionize your business. And somehow, after spending thousands of dollars and countless hours,
              most business owners end up exactly where they started.
            </p>

            <p className="font-display py-2 text-2xl font-light leading-snug text-slate-900 dark:text-white sm:text-3xl">
              So we decided to build the opposite.
            </p>

            <p>
              Symplee wasn&rsquo;t created in a boardroom. It started with a small business owner and a software engineer
              sitting at a kitchen table, looking at a stack of paperwork, a CRM nobody was using, and a growing list of
              software subscriptions that promised everything and delivered very little.
            </p>

            <p>We wrote down what we actually wanted:</p>

            {/* The "what we wanted" stack — line breaks are the rhythm, not bullets. */}
            <div
              className="space-y-3 py-4 font-display text-xl font-light leading-snug text-slate-900 dark:text-white sm:text-2xl"
              data-testid="about-wants-list"
            >
              <p>Save time.</p>
              <p>Make life easier.</p>
              <p>Don&rsquo;t break what already works.</p>
              <p>Tell us the price upfront.</p>
              <p>Let us leave whenever we want.</p>
            </div>

            <p className="font-display py-2 text-2xl font-light text-slate-900 dark:text-white sm:text-3xl">
              That&rsquo;s it.
            </p>

            <p>
              No complicated setup. No consultants. No implementation fees. No training courses. No confusing terminology.
            </p>

            <p className="font-display py-2 text-2xl font-light leading-snug text-slate-900 dark:text-white sm:text-3xl">
              Just turn it on and go.
            </p>

            <p>
              Upload a few documents, connect the tools you already use, and Symplee starts helping immediately. It can answer
              questions about your business, draft content, help with customer communication, create follow-up emails, power a
              website chatbot, and handle dozens of the repetitive tasks that steal hours from your week.
            </p>

            <p className="font-display pt-4 text-xl font-light text-slate-900 dark:text-white sm:text-2xl">
              Not because AI is magical.
            </p>
            <p className="font-display pb-4 text-xl font-light text-slate-900 dark:text-white sm:text-2xl">
              Because your time matters.
            </p>

            <p>
              We don&rsquo;t believe AI is going to change everything overnight. Most business owners aren&rsquo;t looking for
              a revolution. They&rsquo;re looking for a little less stress, a little more time, and fewer things on their
              to-do list.
            </p>

            {/* The "what relief feels like" stack — pull-quote rhythm. */}
            <div
              className="space-y-3 py-4 font-display text-xl font-light leading-snug text-slate-900 dark:text-white sm:text-2xl"
              data-testid="about-relief-list"
            >
              <p>AI shouldn&rsquo;t feel overwhelming.</p>
              <p>It should feel like relief.</p>
              <p>Like finding out the task you&rsquo;ve been putting off is already done.</p>
              <p>Like having a dependable employee who works 24 hours a day and never complains about the boring stuff.</p>
            </div>

            <p>
              That&rsquo;s why Symplee includes everything in one simple plan. No premium tiers hiding the features you actually
              need. No surprise charges. No upgrade fees.
            </p>

            <p>And we run our billing differently, too.</p>

            {/* "Billing differently" stack — same rhythm as wants-list, sibling to it intentionally. */}
            <div
              className="space-y-3 py-4 font-display text-xl font-light leading-snug text-slate-900 dark:text-white sm:text-2xl"
              data-testid="about-billing-list"
            >
              <p>No contracts.</p>
              <p>No late fees.</p>
              <p>No collections agencies.</p>
              <p>No games.</p>
            </div>

            <p data-testid="about-billing-paragraph">
              If you hit a rough month, send us an email. We&rsquo;ll pause your account and pick things back up when
              you&rsquo;re ready. We&rsquo;ve all been there before.
            </p>

            <p>
              Your price is locked in for life. If we raise prices in the future, your rate stays the same.
            </p>

            <p>
              Because we believe earning your business every month is better than trapping you in a contract.
            </p>

            <p className="font-display pt-4 text-xl font-light text-slate-900 dark:text-white sm:text-2xl">
              Symplee isn&rsquo;t built for AI experts.
            </p>
            <p className="font-display pb-2 text-xl font-light text-slate-900 dark:text-white sm:text-2xl">
              It&rsquo;s built for people who are tired of pretending they&rsquo;re supposed to be one.
            </p>

            {/* Closing tagline — gradient brand-text, centered, slightly faded ring of whitespace. */}
            <p
              className="font-display pt-10 text-center text-4xl font-light tracking-tight sm:text-5xl"
              data-testid="about-closing-tagline"
            >
              <span className="brand-text">AI Made Simple</span>
            </p>
          </article>

          <motion.div
            initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-14"
          >
            <Link to="/signup" data-testid="about-cta-primary">
              <Button className="h-12 rounded-full bg-indigo-600 px-7 text-base text-white shadow-sm hover:bg-indigo-700 dark:bg-white dark:text-slate-900 dark:hover:bg-zinc-200">
                Start your 7-day free trial <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <p className="mt-3 text-sm text-slate-500 dark:text-white/55">No credit card required. Cancel anytime. Fully reversible.</p>
          </motion.div>
        </div>
      </section>

      {/* Repeat the 4 pillars for consistency */}
      <section className="relative">
        <div className="mx-auto max-w-6xl px-6 pb-28 sm:px-8 lg:px-12">
          <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">What we promise</div>
          <h2 className="font-display mt-3 text-3xl font-light tracking-tight sm:text-4xl">
            Four small commitments we keep.
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-pillars-grid">
            {PILLARS.map((p, idx) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: idx * 0.06, duration: 0.6, ease: EASE }}
                className="rounded-2xl border border-slate-900/8 bg-white p-6 shadow-sm dark:border-white/8 dark:bg-white/[0.03]"
              >
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-medium text-slate-900 dark:text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-white/60">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-[920px] px-6 pb-20 sm:px-8 lg:px-12">
          <p
            className="text-center text-sm italic text-slate-500 dark:text-white/45"
            data-testid="about-portfolio-line"
          >
            Symplee is an Automation Prime portfolio company.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
